import { useEffect, useMemo, useState } from 'react';
import { Linking, Platform, Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import {
  Download,
  FileText,
  HandCoins,
  History,
  Pencil,
  Plus,
  ShoppingBag,
  Trash2,
} from 'lucide-react-native';
import { exportPetshopExcel, exportPetshopPDF } from '@/services/export';

import { Badge, Button, Card, CatFishPattern, EmptyState, Sheet, SkeletonRows, Tabs, TextField } from '@/components/ui';
import { BarChart3, Ban, Tag } from 'lucide-react-native';
import { CatalogoTab } from './CatalogoTab';
import { ResumenTab } from './ResumenTab';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { ventasPendientes, ventasPetshop, rendicionesPara, anularVentaPetshop } from '@/services/firebase/petshops';
import { usePetshopsMeta } from '@/hooks/usePetshopsMeta';
import { useAuth, selectIsAdmin, selectIsRecepcion } from '@/stores/authStore';
import type { PetshopRendicion, PetshopVenta } from '@/types/domain';
import { PetshopFormSheet } from './PetshopFormSheet';
import { colors, radius, typography } from '@/theme/tokens';
import { formatBs, formatDate, formatDateTime } from '@/utils/format';
import { type MetodoPago, type PetshopId } from '@/types/domain';

import { NuevaVentaSheet } from './NuevaVentaSheet';
import { FiltroPeriodo, enPeriodo, totalesDe, usePeriodo, type Vista } from './periodo';
import { TablaProductos, TablaVentas } from './TablaVentas';
import { RendirSheet } from './RendirSheet';

const METODO_LABEL: Record<MetodoPago, string> = {
  efectivo: 'Efectivo',
  qr: 'QR',
  transferencia: 'Transferencia',
  tarjeta: 'Tarjeta',
  otro: 'Otro',
};

export function PetshopsScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;

  const isAdmin = useAuth(selectIsAdmin);
  const isRecepcion = useAuth(selectIsRecepcion);
  const canManage = isAdmin || isRecepcion;

  const [petshop, setPetshop] = useState<PetshopId>('danna');
  const { petshops, metaDe } = usePetshopsMeta();
  const [petshopForm, setPetshopForm] = useState<{ abrir: boolean; editar: string | null }>({ abrir: false, editar: null });

  // Si el petshop seleccionado no está en la lista (ej. recién borrado), cae al primero.
  useEffect(() => {
    if (petshops.length && !petshops.find((p) => p.id === petshop)) {
      setPetshop(petshops[0].id);
    }
  }, [petshops, petshop]);
  const [tab, setTab] = useState<'ventas' | 'resumen' | 'catalogo'>('ventas');
  const [nuevaVentaOpen, setNuevaVentaOpen] = useState(false);
  const [rendirOpen, setRendirOpen] = useState(false);
  // «Rendir lo elegido»: solo las pendientes del lapso de Ventas (si no, todas).
  const [rendirLapso, setRendirLapso] = useState(false);
  const perfil = useAuth((s) => s.perfil);

  // Anular venta: pide motivo, devuelve stock y la deja registrada como anulada.
  const [anularTarget, setAnularTarget] = useState<PetshopVenta | null>(null);
  const [motivo, setMotivo] = useState('');
  const [anulando, setAnulando] = useState(false);
  const [anularError, setAnularError] = useState<string | null>(null);

  const confirmarAnular = async () => {
    if (!anularTarget || !perfil) return;
    if (!motivo.trim()) { setAnularError('Poné el motivo de la anulación.'); return; }
    setAnulando(true);
    setAnularError(null);
    try {
      await anularVentaPetshop({ ventaId: anularTarget.id, motivo: motivo.trim(), autorUid: perfil.uid, autorNombre: perfil.nombre });
      setAnularTarget(null);
      setMotivo('');
    } catch (e: any) {
      setAnularError(e?.message ?? 'No se pudo anular.');
    } finally {
      setAnulando(false);
    }
  };

  const ventasQ = useMemo(() => ventasPendientes(petshop), [petshop]);
  const rendicionesQ = useMemo(() => rendicionesPara(petshop), [petshop]);

  const ventas = useFirestoreCollection<PetshopVenta>(ventasQ);
  const rendiciones = useFirestoreCollection<PetshopRendicion>(rendicionesQ);

  // La lista de Ventas arranca en «Sin rendir» (lo que se rinde); con otro
  // lapso muestra todas las ventas de esas fechas, rendidas o no.
  const periodoVentas = usePeriodo('pendientes');
  const [vistaVentas, setVistaVentas] = useState<Vista>('ventas');
  const sinRendir = periodoVentas.preset === 'pendientes';
  const todasQ = useMemo(() => (sinRendir ? null : ventasPetshop(petshop)), [petshop, sinRendir]);
  const todas = useFirestoreCollection<PetshopVenta>(todasQ);
  const ventasMostradas = useMemo(
    () => (sinRendir ? ventas.data : enPeriodo(todas.data, periodoVentas.desde, periodoVentas.hasta)),
    [sinRendir, ventas.data, todas.data, periodoVentas.desde, periodoVentas.hasta],
  );
  const cargandoVentas = sinRendir ? ventas.loading : todas.loading;
  const totalesVentas = useMemo(() => totalesDe(ventasMostradas), [ventasMostradas]);
  const pendientesLapso = useMemo(
    () => (sinRendir ? [] : ventasMostradas.filter((v) => !v.anulada && !v.rendicionId)),
    [sinRendir, ventasMostradas],
  );
  const totalLapso = pendientesLapso.reduce((s, v) => s + (v.monto ?? 0), 0);

  // El total NO cuenta las ventas anuladas.
  const total = useMemo(() => ventas.data.reduce((sum, v) => sum + (v.anulada ? 0 : v.monto ?? 0), 0), [ventas.data]);
  const cantidadSinRendir = useMemo(() => ventas.data.filter((v) => !v.anulada).length, [ventas.data]);

  const meta = metaDe(petshop);

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 24 : 12, gap: 16, paddingBottom: 80 }}>
      {/* Selector de petshop + exportar arriba a la derecha */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <View
          style={{
            flexDirection: 'row',
            gap: 4,
            flexWrap: 'wrap',
            padding: 5,
            backgroundColor: colors.white,
            borderRadius: radius.full,
            borderWidth: 1,
            borderColor: colors.ink[100],
          }}
        >
          {petshops.map((p) => {
            const active = petshop === p.id;
            return (
              <Pressable
                key={p.id}
                onPress={() => setPetshop(p.id)}
                style={({ hovered }) => ({
                  paddingHorizontal: 18,
                  paddingVertical: 8,
                  borderRadius: 999,
                  backgroundColor: active ? colors.magenta[700] : hovered ? colors.crema[50] : 'transparent',
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
              >
                <Text style={{ color: active ? colors.white : colors.ink[700], fontWeight: typography.weights.bold, fontSize: 13 }}>
                  {p.nombreCorto || p.nombre}
                </Text>
              </Pressable>
            );
          })}
          {isAdmin ? (
            <Pressable
              onPress={() => setPetshopForm({ abrir: true, editar: null })}
              title="Agregar petshop"
              style={({ hovered }) => ({
                paddingHorizontal: 14,
                paddingVertical: 8,
                borderRadius: 999,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                backgroundColor: hovered ? colors.magenta[50] : 'transparent',
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <Plus size={14} color={colors.magenta[700]} />
              <Text style={{ color: colors.magenta[700], fontWeight: typography.weights.bold, fontSize: 13 }}>Nuevo</Text>
            </Pressable>
          ) : null}
        </View>

        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
          {isAdmin ? (
            <Pressable
              onPress={() => setPetshopForm({ abrir: true, editar: petshop })}
              title="Editar este petshop"
              style={({ hovered }) => ({
                width: 38, height: 38, borderRadius: radius.xl, alignItems: 'center', justifyContent: 'center',
                backgroundColor: hovered ? colors.ink[100] : colors.white, borderWidth: 1, borderColor: colors.ink[200],
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <Pencil size={15} color={colors.ink[600]} />
            </Pressable>
          ) : null}
          <Pressable
            onPress={async () => {
              try {
                await exportPetshopExcel({ petshop, petshopNombre: meta.nombre, petshopCorto: meta.corto, ventas: ventasMostradas.filter((v) => !v.anulada), rendiciones: rendiciones.data, periodoTxt: periodoVentas.texto });
              } catch (e: any) {
                alert(e?.message ?? 'No se pudo exportar.');
              }
            }}
            style={({ hovered }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
              paddingHorizontal: 14,
              height: 38,
              borderRadius: radius.xl,
              backgroundColor: hovered ? colors.success[600] : colors.success[500],
              // @ts-ignore web only
              cursor: 'pointer',
            })}
          >
            <Download size={14} color={colors.white} />
            <Text style={{ color: colors.white, fontWeight: typography.weights.bold, fontSize: 12 }}>Excel</Text>
          </Pressable>
          <Pressable
            onPress={async () => {
              try {
                await exportPetshopPDF({ petshop, petshopNombre: meta.nombre, petshopCorto: meta.corto, ventas: ventasMostradas.filter((v) => !v.anulada), rendiciones: rendiciones.data, periodoTxt: periodoVentas.texto });
              } catch (e: any) {
                alert(e?.message ?? 'No se pudo exportar.');
              }
            }}
            style={({ hovered }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
              paddingHorizontal: 14,
              height: 38,
              borderRadius: radius.xl,
              backgroundColor: hovered ? colors.danger[600] : colors.danger[500],
              // @ts-ignore web only
              cursor: 'pointer',
            })}
          >
            <FileText size={14} color={colors.white} />
            <Text style={{ color: colors.white, fontWeight: typography.weights.bold, fontSize: 12 }}>PDF</Text>
          </Pressable>
        </View>
      </View>

      {/* Hero del período activo */}
      <View
        style={{
          padding: wide ? 24 : 18,
          borderRadius: radius['3xl'],
          backgroundColor: colors.magenta[700],
          // @ts-ignore web only
          backgroundImage: `radial-gradient(800px 300px at 110% -10%, ${colors.magenta[500]}88, transparent 60%), linear-gradient(135deg, ${colors.magenta[800]} 0%, ${colors.magenta[600]} 100%)`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <CatFishPattern variant="magenta" opacity={0.3} tileSize={300} />
        <View style={{ flexDirection: wide ? 'row' : 'column', alignItems: wide ? 'center' : 'flex-start', gap: 18 }}>
          <View style={{ flex: 1, gap: 4 }}>
            <Text
              style={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: 11,
                fontWeight: typography.weights.bold,
                letterSpacing: 1.4,
                textTransform: 'uppercase',
              }}
            >
              Acumulado del período · {meta.nombre}
            </Text>
            <Text
              style={{
                color: colors.white,
                fontFamily: typography.display.fontFamily,
                fontSize: wide ? 44 : 34,
                fontWeight: typography.weights.bold,
                letterSpacing: -0.8,
              }}
              numberOfLines={1}
            >
              {formatBs(total)}
            </Text>
            <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
              {cantidadSinRendir} {cantidadSinRendir === 1 ? 'venta sin rendir' : 'ventas sin rendir'}
            </Text>
          </View>

          <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
            {canManage ? (
              <Pressable
                onPress={() => setNuevaVentaOpen(true)}
                style={({ hovered }) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  paddingHorizontal: 18,
                  height: 44,
                  borderRadius: radius.xl,
                  backgroundColor: hovered ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.12)',
                  borderWidth: 1,
                  borderColor: 'rgba(255,255,255,0.2)',
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
              >
                <Plus size={16} color={colors.white} />
                <Text style={{ color: colors.white, fontWeight: typography.weights.bold, fontSize: 13 }}>Nueva venta</Text>
              </Pressable>
            ) : null}
            {canManage && ventas.data.length > 0 ? (
              <Pressable
                onPress={() => {
                  setRendirLapso(false);
                  setRendirOpen(true);
                }}
                style={({ hovered }) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  paddingHorizontal: 18,
                  height: 44,
                  borderRadius: radius.xl,
                  backgroundColor: hovered ? colors.success[600] : colors.success[500],
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
              >
                <HandCoins size={16} color={colors.white} />
                <Text style={{ color: colors.white, fontWeight: typography.weights.bold, fontSize: 13 }}>
                  Rendir a {meta.corto}
                </Text>
              </Pressable>
            ) : null}
          </View>
        </View>
      </View>

      {/* Tabs internos */}
      <Tabs
        active={tab}
        onChange={(t) => setTab(t as 'ventas' | 'resumen' | 'catalogo')}
        items={[
          { id: 'catalogo', label: 'Catálogo', icon: Tag },
          { id: 'ventas', label: 'Ventas y rendiciones', icon: ShoppingBag, badge: ventas.data.length, badgeTone: 'magenta' },
          { id: 'resumen', label: 'Evaluar ventas', icon: BarChart3 },
        ]}
      />

      {tab === 'resumen' ? <ResumenTab petshop={petshop} /> : null}
      {tab === 'catalogo' ? <CatalogoTab petshop={petshop} /> : null}

      {/* Tabla de ventas pendientes */}
      {tab === 'ventas' ? (
      <>
      <Card padding={0} style={{ overflow: 'hidden' }}>
        <View style={{ paddingHorizontal: 18, paddingTop: 16, paddingBottom: 12, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <ShoppingBag size={18} color={colors.magenta[700]} />
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
              Ventas del período
            </Text>
          </View>
          <Badge label={`${ventasMostradas.length}`} tone="magenta" />
        </View>

        <FiltroPeriodo
          periodo={periodoVentas}
          conPendientes
          vista={{ valor: vistaVentas, cambiar: setVistaVentas }}
          acciones={
            canManage && pendientesLapso.length > 0 ? (
              <Button
                label={`Rendir lo elegido · ${formatBs(totalLapso)}`}
                variant="success"
                size="sm"
                iconLeft={<HandCoins size={14} color={colors.white} />}
                onPress={() => {
                  setRendirLapso(true);
                  setRendirOpen(true);
                }}
              />
            ) : null
          }
        />

        {cargandoVentas ? (
          <View style={{ padding: 16 }}>
            <SkeletonRows rows={4} />
          </View>
        ) : ventasMostradas.length === 0 ? (
          <View style={{ padding: 12 }}>
            <EmptyState
              illustration="note"
              title="Sin ventas en este período"
              message={
                canManage
                  ? 'Tocá «Nueva venta» para registrar la primera.'
                  : 'Las ventas se mostrarán acá a medida que la recepción las registre.'
              }
              actionLabel={canManage ? 'Nueva venta' : undefined}
              onAction={canManage ? () => setNuevaVentaOpen(true) : undefined}
            />
          </View>
        ) : (
          vistaVentas === 'productos' ? (
            <TablaProductos ventas={ventasMostradas} />
          ) : (
            <TablaVentas
              ventas={ventasMostradas}
              totales={totalesVentas}
              anular={{
                puede: canManage,
                alTocar: (v) => {
                  setAnularTarget(v);
                  setMotivo('');
                  setAnularError(null);
                },
              }}
            />
          )
        )}
      </Card>

      {/* Histórico de rendiciones */}
      <Card padding={0} style={{ overflow: 'hidden' }}>
        <View style={{ paddingHorizontal: 18, paddingTop: 16, paddingBottom: 12, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <History size={18} color={colors.magenta[700]} />
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
              Histórico de rendiciones
            </Text>
          </View>
          <Badge label={`${rendiciones.data.length}`} tone="neutral" />
        </View>

        {rendiciones.loading ? (
          <View style={{ padding: 16 }}>
            <SkeletonRows rows={3} />
          </View>
        ) : rendiciones.data.length === 0 ? (
          <View style={{ paddingVertical: 28, alignItems: 'center', gap: 4 }}>
            <Text style={{ color: colors.ink[500], fontSize: 13 }}>Aún no rendiste ningún período.</Text>
          </View>
        ) : wide ? (
          <View>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingHorizontal: 18,
                paddingVertical: 10,
                backgroundColor: colors.crema[50],
                borderTopWidth: 1,
                borderBottomWidth: 1,
                borderTopColor: colors.ink[100],
                borderBottomColor: colors.ink[100],
                gap: 14,
              }}
            >
              <ColTitle text="Período" width={120} />
              <ColTitle text="Ventas" width={70} align="right" />
              <ColTitle text="Total" width={130} align="right" />
              <ColTitle text="Rendida" flex={1} />
              <ColTitle text="Comprobante" width={140} />
            </View>

            {rendiciones.data.map((r, idx) => (
              <View
                key={r.id}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingHorizontal: 18,
                  paddingVertical: 12,
                  gap: 14,
                  backgroundColor: idx % 2 === 1 ? 'rgba(250,247,244,0.5)' : colors.white,
                  borderBottomWidth: idx === rendiciones.data.length - 1 ? 0 : 1,
                  borderBottomColor: colors.ink[100],
                }}
              >
                <Text style={{ width: 120, fontSize: 13, color: colors.ink[900], fontWeight: typography.weights.bold }}>
                  {nombreMes(r.mesISO ?? (r as any).mesIso)}
                </Text>
                <Text style={{ width: 70, textAlign: 'right', fontSize: 13, color: colors.ink[700] }}>{r.cantidadVentas}</Text>
                <Text
                  style={{
                    width: 130,
                    textAlign: 'right',
                    fontFamily: typography.display.fontFamily,
                    fontSize: 15,
                    fontWeight: typography.weights.bold,
                    color: colors.magenta[700],
                  }}
                >
                  {formatBs(r.total)}
                </Text>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 13, color: colors.ink[800] }}>
                    {formatDateTime(r.rendidaEn?.toDate?.() ?? null)}
                  </Text>
                  <Text style={{ fontSize: 11, color: colors.ink[500] }}>
                    {METODO_LABEL[r.metodoTransferencia]} · {r.rendidaPorNombre}
                  </Text>
                </View>
                <View style={{ width: 140 }}>
                  {r.comprobanteUrl ? (
                    <Pressable
                      onPress={() => Linking.openURL(r.comprobanteUrl!)}
                      style={({ hovered }) => ({
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 6,
                        paddingHorizontal: 10,
                        height: 28,
                        borderRadius: 8,
                        backgroundColor: hovered ? colors.magenta[100] : colors.magenta[50],
                        alignSelf: 'flex-start',
                        // @ts-ignore web only
                        cursor: 'pointer',
                      })}
                    >
                      <FileText size={12} color={colors.magenta[700]} />
                      <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.magenta[700] }}>
                        Ver comprobante
                      </Text>
                    </Pressable>
                  ) : (
                    <Text style={{ color: colors.ink[400], fontSize: 12 }}>Sin comprobante</Text>
                  )}
                </View>
              </View>
            ))}
          </View>
        ) : (
          // Celular: una tarjeta por rendición (en vez de una tabla angosta)
          <View style={{ padding: 14, gap: 10 }}>
            {rendiciones.data.map((r) => (
              <View
                key={r.id}
                style={{
                  padding: 14,
                  borderRadius: radius.xl,
                  borderWidth: 1,
                  borderColor: colors.ink[100],
                  backgroundColor: colors.white,
                  gap: 6,
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <Text style={{ fontSize: 15, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
                    {nombreMes(r.mesISO ?? (r as any).mesIso)}
                  </Text>
                  <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.magenta[700] }}>
                    {formatBs(r.total)}
                  </Text>
                </View>
                <Text style={{ fontSize: 12.5, color: colors.ink[600] }}>
                  {r.cantidadVentas} {r.cantidadVentas === 1 ? 'venta' : 'ventas'} · {METODO_LABEL[r.metodoTransferencia]} · {r.rendidaPorNombre}
                </Text>
                <Text style={{ fontSize: 11.5, color: colors.ink[400] }}>
                  {formatDateTime(r.rendidaEn?.toDate?.() ?? null)}
                </Text>
                {r.comprobanteUrl ? (
                  <Pressable
                    onPress={() => Linking.openURL(r.comprobanteUrl!)}
                    style={({ hovered }) => ({
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 6,
                      paddingHorizontal: 10,
                      height: 30,
                      borderRadius: 8,
                      backgroundColor: hovered ? colors.magenta[100] : colors.magenta[50],
                      alignSelf: 'flex-start',
                      // @ts-ignore web only
                      cursor: 'pointer',
                    })}
                  >
                    <FileText size={12} color={colors.magenta[700]} />
                    <Text style={{ fontSize: 11.5, fontWeight: typography.weights.bold, color: colors.magenta[700] }}>
                      Ver comprobante
                    </Text>
                  </Pressable>
                ) : (
                  <Text style={{ color: colors.ink[400], fontSize: 12 }}>Sin comprobante</Text>
                )}
              </View>
            ))}
          </View>
        )}
      </Card>
      </>
      ) : null}

      <NuevaVentaSheet visible={nuevaVentaOpen} onClose={() => setNuevaVentaOpen(false)} petshop={petshop} petshopNombre={meta.nombre} />
      <RendirSheet
        visible={rendirOpen}
        onClose={() => setRendirOpen(false)}
        petshop={petshop}
        petshopCorto={meta.corto}
        qrUrl={meta.qrUrl}
        telefono={meta.telefono}
        dueno={meta.dueno}
        total={rendirLapso ? totalLapso : total}
        cantidad={rendirLapso ? pendientesLapso.length : cantidadSinRendir}
        ventaIds={rendirLapso ? pendientesLapso.map((v) => v.id) : undefined}
        periodoTxt={rendirLapso ? periodoVentas.texto : undefined}
      />
      <PetshopFormSheet
        visible={petshopForm.abrir}
        onClose={(nuevoId) => { setPetshopForm({ abrir: false, editar: null }); if (nuevoId) setPetshop(nuevoId); }}
        petshop={petshopForm.editar ? metaDe(petshopForm.editar) : null}
      />

      {/* Anular venta: pide motivo, devuelve stock y la deja registrada */}
      <Sheet
        visible={!!anularTarget}
        onClose={() => { if (!anulando) { setAnularTarget(null); setMotivo(''); setAnularError(null); } }}
        size="sm"
        title="Anular venta"
        subtitle={anularTarget?.descripcion}
        footer={
          <>
            <Button label="Cancelar" variant="ghost" onPress={() => { setAnularTarget(null); setMotivo(''); setAnularError(null); }} disabled={anulando} />
            <Button label="Anular venta" variant="danger" iconLeft={<Ban size={15} color={colors.white} />} onPress={confirmarAnular} loading={anulando} />
          </>
        }
      >
        <View style={{ gap: 12 }}>
          <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.warning[50] }}>
            <Text style={{ fontSize: 13, color: colors.ink[700] }}>
              Se va a devolver el stock al catálogo y este ingreso de {anularTarget ? formatBs(anularTarget.monto) : ''} dejará de contar. La venta queda registrada como anulada (no se borra).
            </Text>
          </View>
          <TextField
            label="Motivo de la anulación"
            placeholder="Ej: se registró por error"
            value={motivo}
            onChangeText={setMotivo}
            autoFocus
          />
          {anularError ? (
            <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
              <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{anularError}</Text>
            </View>
          ) : null}
        </View>
      </Sheet>
    </ScrollView>
  );
}

function ColTitle({
  text,
  flex,
  width,
  align,
}: {
  text: string;
  flex?: number;
  width?: number;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <Text
      style={{
        flex,
        width,
        color: colors.ink[500],
        fontSize: 11,
        fontWeight: typography.weights.bold,
        letterSpacing: 1.4,
        textTransform: 'uppercase',
        textAlign: align ?? 'left',
      }}
    >
      {text}
    </Text>
  );
}

function nombreMes(iso?: string | null): string {
  if (!iso) return '—';
  const [y, m] = String(iso).split('-');
  const nombres = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const mi = Number(m) - 1;
  return `${nombres[mi] ?? m} ${y ?? ''}`;
}
